import { QueryFunctionOptions, QueryResult } from '@apollo/client';
import {
  Language,
  LanguageId,
  OrganizationSettingsQuery,
  OrganizationSettingsQueryVariables,
  useOrganizationSettingsQuery,
} from 'lib-knowledge-management-common/graphql';
import { MarkOptional } from 'ts-essentials';

export type LanguageModel = MarkOptional<Language, '__typename' | 'usage'>;

export type UseManagementLanguagesParams = {
  selectedLanguageId?: LanguageId | null;
  onCompleted?: QueryFunctionOptions<OrganizationSettingsQuery>['onCompleted'];
  fetchPolicy?: QueryFunctionOptions<OrganizationSettingsQuery>['fetchPolicy'];
};

export type UseManagementLanguagesData =
  OrganizationSettingsQuery['organisationSettings'] & {
    selectedLanguage: LanguageModel;
  };

export type UseManagementLanguagesReturn = Omit<
  QueryResult<OrganizationSettingsQuery, OrganizationSettingsQueryVariables>,
  'data'
> & {
  data: UseManagementLanguagesData | undefined;
};

export function useManagementLanguages({
  selectedLanguageId,
  onCompleted,
  fetchPolicy = 'cache-first',
}: UseManagementLanguagesParams = {}): UseManagementLanguagesReturn {
  const { data, ...res } = useOrganizationSettingsQuery({
    fetchPolicy,
    onCompleted,
  });

  return { ...res, data: transformLanguageData(data, selectedLanguageId) };
}

function transformLanguageData(
  data: OrganizationSettingsQuery | undefined,
  selectedLanguageId: LanguageId | undefined | null,
): UseManagementLanguagesData | undefined {
  if (!data?.organisationSettings) return undefined;

  // Always default to the default language if no language is selected or is not found
  const selectedLanguage: LanguageModel =
    data.organisationSettings.enabledLanguages.find(
      (lang) => lang.languageId === selectedLanguageId,
    ) || data.organisationSettings.defaultLanguage;

  return { ...data.organisationSettings, selectedLanguage };
}
